/**
 * Vite app root. Default export only because Vite expects it for the
 * entry module (see CLAUDE-react.md §Naming + exports + imports). All
 * other modules in the app use named exports.
 *
 * Provider order: QueryClient → I18n → (dev-only DevProviders) → Router.
 * The DevProviders subtree is dynamically imported behind `import.meta.env.DEV`
 * in main.tsx, so prod builds drop the whole branch.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { AppShell } from './AppShell'
import { ActionToastProvider } from './components/ActionToast'
import { SessionProvider } from './auth/SessionContext'
import { HealthGate } from './features/auth/HealthGate'
import { LoginRoute } from './features/auth/LoginRoute'
import { TodayRoute } from './features/today/TodayRoute'
import { WorkOrderDetailRoute } from './features/work-order/WorkOrderDetailRoute'
import { InventoryRoute } from './features/inventory/InventoryRoute'
import { ServicesRoute } from './features/services/ServicesRoute'
import { ProfileRoute } from './features/profile/ProfileRoute'
import { TranslationProvider } from './i18n/TranslationContext'
import { CurtainTransitionProvider } from './transitions/CurtainTransition'

interface AppProps {
  /** Optional dev-only wrapper. Injected by `main.tsx` in dev builds only. */
  readonly devWrapper?: (node: ReactNode) => ReactNode
}

export default function App({ devWrapper }: AppProps = {}) {
  const [client] = useState(
    () => new QueryClient({
      defaultOptions: {
        queries: {
          // Live polling app-wide, matching the management app. Without
          // it the technician's bell only refreshed on a tab refocus or
          // a remount — so a manager accepting or declining a part
          // request could sit unseen indefinitely, which is the normal
          // case when the screen is left open on a running task.
          //
          // React Query pauses the interval while the tab is hidden
          // (`refetchIntervalInBackground` defaults to false), so
          // background tabs don't poll.
          refetchInterval: 30_000,
          staleTime: 15_000,
          refetchOnWindowFocus: true,
          retry: 1,
        },
      },
    }),
  )

  const tree = (
    <BrowserRouter>
      <Routes>
        {/* HealthGate sits in front of the login screen specifically.
            Authenticated routes already wait for `/users/me` via AuthGuard;
            adding a second health probe there would double the wait. */}
        <Route
          path="/login"
          element={
            <HealthGate>
              <LoginRoute />
            </HealthGate>
          }
        />
        <Route element={<CurtainTransitionProvider><AppShell /></CurtainTransitionProvider>}>
          <Route index element={<TodayRoute />} />
          <Route path="/inventory" element={<InventoryRoute />} />
          <Route path="/services" element={<ServicesRoute />} />
          <Route path="/profile" element={<ProfileRoute />} />
          <Route path="/work-orders/:id" element={<WorkOrderDetailRoute />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )

  return (
    <QueryClientProvider client={client}>
      <TranslationProvider>
        {/* Inside i18n (it renders copy), outside the router — a failed
            action must survive the tech navigating away from the row. */}
        <ActionToastProvider>
          <SessionProvider>
            {devWrapper ? devWrapper(tree) : tree}
          </SessionProvider>
        </ActionToastProvider>
      </TranslationProvider>
    </QueryClientProvider>
  )
}
